import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Mic, Search, X } from "lucide-react";

const LOOK = {
  beast: {
    panel: "bg-neutral-950 text-white border-white/15",
    input: "bg-black border-white/20 text-white placeholder:text-white/40 focus:ring-white/30",
    btn: "bg-white text-black hover:bg-white/90",
    select: "bg-black text-white border-white/20",
    muted: "text-white/50",
    ink: "text-white",
    iconBtn: "text-white hover:bg-white/10",
    overlay: "bg-black/70",
  },
  boxy: {
    panel: "bg-[#F7F4EE] text-[color:var(--store-primary)] border-[color:color-mix(in_srgb,var(--store-primary)_15%,transparent)]",
    input: "bg-white border-[color:color-mix(in_srgb,var(--store-primary)_20%,transparent)] text-[color:var(--store-primary)] placeholder:text-[color:color-mix(in_srgb,var(--store-primary)_40%,transparent)] focus:ring-[color:color-mix(in_srgb,var(--store-primary)_25%,transparent)]",
    btn: "bg-[var(--store-primary)] text-white hover:opacity-90",
    select: "bg-white text-[color:var(--store-primary)] border-[color:color-mix(in_srgb,var(--store-primary)_20%,transparent)]",
    muted: "text-[color:color-mix(in_srgb,var(--store-primary)_55%,transparent)]",
    ink: "text-[color:var(--store-primary)]",
    iconBtn: "text-[color:var(--store-primary)] hover:opacity-70",
    overlay: "bg-black/45",
  },
  medico: {
    panel: "bg-white text-[color:var(--store-primary)] border-teal-100",
    input: "bg-[#F5FBF7] border-teal-100 text-stone-800 placeholder:text-stone-400 focus:ring-teal-200",
    btn: "bg-[var(--store-primary)] text-white hover:opacity-90",
    select: "bg-[#F5FBF7] text-stone-800 border-teal-100",
    muted: "text-stone-500",
    ink: "text-[color:var(--store-primary)]",
    iconBtn: "text-[color:var(--store-primary)] hover:opacity-70",
    overlay: "bg-teal-950/45",
  },
  perfecto: {
    panel: "bg-white text-[#3E2723] border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-stone-300",
    btn: "bg-[#3E2723] text-white hover:bg-[#2c1c19]",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-[#3E2723]",
    iconBtn: "text-[#3E2723] hover:opacity-70",
    overlay: "bg-stone-950/50",
  },
  mixy: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-[#F3F4F6] border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-stone-300",
    btn: "mixy-accent-fill text-stone-900 hover:opacity-90",
    select: "bg-[#F3F4F6] text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-stone-900",
    iconBtn: "text-stone-800 hover:bg-stone-100",
    overlay: "bg-stone-950/50",
  },
  various: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-[#F1F2E8] border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-[color:color-mix(in_srgb,var(--store-primary)_30%,transparent)]",
    btn: "bg-[var(--store-primary)] text-white hover:opacity-90",
    select: "bg-[#F1F2E8] text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-stone-900",
    iconBtn: "text-stone-800 hover:bg-stone-100",
    overlay: "bg-black/50",
  },
  locally: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-teal-200",
    btn: "bg-[var(--store-primary)] text-white hover:opacity-90",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-[color:var(--store-primary)]",
    iconBtn: "text-[color:var(--store-primary)] hover:bg-stone-100",
    overlay: "bg-black/45",
  },
  quicksto: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-white border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-[color:color-mix(in_srgb,var(--store-secondary)_40%,transparent)]",
    btn: "bg-[var(--store-secondary)] text-[color:var(--store-primary)] hover:opacity-90",
    select: "bg-white text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-[#111111]",
    iconBtn: "text-white hover:bg-white/10",
    overlay: "bg-black/55",
  },
  bigshow: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-[#C8F04D]/50",
    btn: "bg-[#111111] text-white hover:bg-stone-800",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-[#111111]",
    iconBtn: "text-stone-800 hover:bg-stone-100",
    overlay: "bg-black/50",
  },
  furnis: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-[#B08D57]/40",
    btn: "bg-[#1C1917] text-white hover:bg-stone-800",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-[#1C1917]",
    iconBtn: "text-stone-800 hover:bg-stone-100",
    overlay: "bg-black/45",
  },
  carparts: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-[#F25C05]/40",
    btn: "bg-[#1B2430] text-white hover:bg-stone-800",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-[#1B2430]",
    iconBtn: "text-stone-800 hover:bg-stone-100",
    overlay: "bg-black/45",
  },
  cosmos: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-sky-300",
    btn: "bg-[var(--store-primary,#2563EB)] text-white hover:opacity-90",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-stone-900",
    iconBtn: "text-stone-800 hover:bg-stone-100",
    overlay: "bg-black/40",
  },
  ondemand: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-violet-300",
    btn: "bg-[var(--store-primary,#7C3AED)] text-white hover:opacity-90",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-stone-900",
    iconBtn: "text-stone-800 hover:bg-stone-100",
    overlay: "bg-black/40",
  },
  trendy: {
    panel: "bg-white text-stone-900 border-rose-100",
    input: "bg-rose-50/60 border-rose-100 text-stone-800 placeholder:text-stone-400 focus:ring-rose-200",
    btn: "bg-rose-600 text-white hover:bg-rose-700",
    select: "bg-rose-50 text-stone-800 border-rose-100",
    muted: "text-stone-500",
    ink: "text-stone-900",
    iconBtn: "text-stone-600 hover:text-rose-700 hover:bg-rose-50",
    overlay: "bg-stone-950/50",
  },
  modern: {
    panel: "bg-white text-neutral-950 border-neutral-200",
    input: "bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:ring-neutral-300",
    btn: "bg-neutral-950 text-white hover:bg-neutral-800",
    select: "bg-neutral-50 text-neutral-900 border-neutral-200",
    muted: "text-neutral-500",
    ink: "text-neutral-950",
    iconBtn: "text-neutral-700 hover:text-neutral-950 hover:bg-neutral-100",
    overlay: "bg-neutral-950/55",
  },
  classic: {
    panel: "bg-white text-stone-900 border-stone-200",
    input: "bg-stone-50 border-stone-200 text-stone-800 placeholder:text-stone-400 focus:ring-stone-300",
    btn: "bg-stone-900 text-white hover:bg-stone-800",
    select: "bg-stone-50 text-stone-800 border-stone-200",
    muted: "text-stone-500",
    ink: "text-stone-800",
    iconBtn: "text-stone-600 hover:text-stone-900 hover:bg-stone-100",
    overlay: "bg-stone-950/55",
  },
};

function lookTheme(look) {
  return LOOK[look] || LOOK.classic;
}

export function StorefrontSearchPopup({
  open,
  onClose,
  shopHref,
  navigate,
  look = "classic",
  testId = "store-search-overlay",
  placeholder = "Search products",
  categories,
  showCategory = false,
  allLabel = "All products",
  showMic = false,
}) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const inputRef = useRef(null);
  const theme = lookTheme(look);

  useEffect(() => {
    if (!open) return undefined;
    setQ("");
    setCat("");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const go = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    const query = q.trim();
    if (query) params.set("q", query);
    if (cat) params.set("category", cat);
    const qs = params.toString();
    navigate(`${shopHref}${qs ? `?${qs}` : ""}`);
    onClose();
  };

  const roots = (categories || []).filter((c) => !c.parent_id);
  const kidsOf = (id) => (categories || []).filter((c) => c.parent_id === id);
  const catOptions = roots.flatMap((c) => [{ slug: c.slug, name: c.name }, ...kidsOf(c.id).map((ch) => ({ slug: ch.slug, name: `— ${ch.name}` }))]);

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-stretch sm:items-start md:items-center justify-center p-0 sm:p-6 md:p-10" data-testid={`${testId}-overlay`} role="dialog" aria-modal="true" aria-label="Search products">
      <style>{`
        @keyframes sfSearchFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes sfSearchSheet { from { opacity: 0; transform: translateY(-18px) } to { opacity: 1; transform: none } }
        @keyframes sfSearchPop { from { opacity: 0; transform: translateY(12px) scale(.97) } to { opacity: 1; transform: none } }
        .sf-search-fade { animation: sfSearchFade .22s ease-out }
        .sf-search-panel { animation: sfSearchSheet .3s cubic-bezier(.22,1,.36,1) }
        @media (min-width: 768px) {
          .sf-search-panel { animation-name: sfSearchPop }
        }
      `}</style>
      <button type="button" className={`sf-search-fade absolute inset-0 backdrop-blur-md ${theme.overlay}`} aria-label="Close search" onClick={onClose} data-testid={`${testId}-backdrop`} />
      <div className={`sf-search-panel relative w-full md:max-w-2xl lg:max-w-3xl mx-auto self-start md:self-auto rounded-none sm:rounded-3xl border shadow-[0_24px_80px_rgba(0,0,0,.28)] ${theme.panel} px-4 pt-[max(0.85rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-7`}>
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className={`text-xl sm:text-2xl font-semibold tracking-tight ${theme.ink}`}>Search</div>
            <p className={`text-xs sm:text-sm mt-1 ${theme.muted}`}>Find products across this store</p>
          </div>
          <button type="button" onClick={onClose} className={`inline-flex items-center justify-center w-11 h-11 rounded-full shrink-0 ${theme.iconBtn}`} aria-label="Close" data-testid={`${testId}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={go} data-testid={`${testId}-form`} className="space-y-3">
          {showCategory ? (
            <label className="block">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${theme.muted}`}>Category</span>
              <span className="relative mt-1.5 flex">
                <select
                  value={cat}
                  onChange={(e) => setCat(e.target.value)}
                  data-testid={`${testId}-category`}
                  className={`w-full h-12 sm:h-14 appearance-none rounded-2xl border pl-4 pr-10 text-sm font-semibold outline-none ${theme.select}`}
                >
                  <option value="">{allLabel}</option>
                  {catOptions.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
                <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.muted}`} />
              </span>
            </label>
          ) : null}
          <div className={`flex items-stretch rounded-2xl border overflow-hidden shadow-inner ${theme.input}`}>
            <span className={`w-12 sm:w-14 shrink-0 inline-flex items-center justify-center ${theme.muted}`} aria-hidden>
              <Search className="w-5 h-5" />
            </span>
            <input
              ref={inputRef}
              data-testid={`${testId}-input`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              enterKeyHint="search"
              autoComplete="off"
              className={`flex-1 min-w-0 h-14 sm:h-16 bg-transparent border-0 px-0 pr-2 text-base sm:text-xl outline-none ${theme.input}`}
            />
            {showMic ? <span className={`w-10 shrink-0 inline-flex items-center justify-center ${theme.muted}`} aria-hidden><Mic className="w-4 h-4" /></span> : null}
            <button type="submit" aria-label="Search" data-testid={`${testId}-submit`} className={`m-1.5 px-4 sm:px-6 rounded-xl text-sm font-bold shrink-0 ${theme.btn}`}>
              Search
            </button>
          </div>
        </form>
        <p className={`mt-4 text-[11px] sm:text-xs ${theme.muted}`}>Press Enter to search · Esc to close</p>
      </div>
    </div>,
    document.body,
  );
}

export function StorefrontSearchTrigger({
  shopHref,
  navigate,
  look = "classic",
  testId = "store-search-toggle",
  placeholder = "Search products",
  categories,
  showCategory = false,
  allLabel = "All products",
  showMic = false,
  className,
  field = false,
}) {
  const [open, setOpen] = useState(false);
  const theme = lookTheme(look);
  const editorial = look === "perfecto" || look === "boxy" || look === "medico" || look === "beast";
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        aria-label="Search"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className={className || (field
          ? "w-full h-12 rounded-2xl border border-stone-200 bg-stone-50 px-4 inline-flex items-center gap-3 text-left text-stone-500 hover:border-stone-300"
          : editorial
            ? `relative inline-flex items-center justify-center w-10 h-10 ${theme.iconBtn}`
            : "relative inline-flex items-center justify-center w-10 h-10 rounded-full text-stone-600 hover:text-stone-900 hover:bg-stone-100")}
      >
        <Search className="w-5 h-5 shrink-0" strokeWidth={look === "boxy" ? 1.5 : 2} />
        {field ? <span className="truncate text-sm">{placeholder}</span> : null}
      </button>
      <StorefrontSearchPopup
        open={open}
        onClose={() => setOpen(false)}
        shopHref={shopHref}
        navigate={navigate}
        look={look}
        testId={testId}
        placeholder={placeholder}
        categories={categories}
        showCategory={showCategory}
        allLabel={allLabel}
        showMic={showMic}
      />
    </>
  );
}
